import type { RequiredGeneric } from '@pba/utils';
import { Checkbox, Col, Form, Input, Row, Switch, Text, Title } from 'ui';
import { useUser } from 'features/auth';
import { useGivingCreate } from '../../hooks';
import { PayerInformation } from '../payer-information';
import { AddressFormContent, type AddressValues } from './address-form-content';

export type BillingInformationValues = CustomBillingInformation | UserBillingInformation;

type CustomBillingInformation = {
  email: string;
  firstName: string;
  lastName: string;
  profileAddress?: false;
  profileUpdate?: boolean;
} & AddressValues;

type P = { hasViewerCompleteAddress: boolean };

type UserBillingInformation = {
  profileAddress: true;
};

const BillingInformation = <T extends BillingInformationValues = never>({
  hasViewerCompleteAddress,
}: RequiredGeneric<T, P>) => {
  const user = useUser({ requiredAuth: false });
  const { payerInformation } = useGivingCreate();

  return (
    <Row gutter={[0, 'ms']}>
      <Col span={24}>
        <Title level={4} marginBottom>
          Billing information
        </Title>
      </Col>
      {hasViewerCompleteAddress ? (
        <Col span={24}>
          <Form.Item<BillingInformationValues>>
            <Row gutter="xs">
              <Col>
                <Form.Item<BillingInformationValues> name="profileAddress" noStyle valuePropName="checked">
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col>
                <Text size="small">Use the address in my profile for billing</Text>
              </Col>
            </Row>
          </Form.Item>
        </Col>
      ) : null}
      <Col span={24}>
        <Form.Item<BillingInformationValues> dependencies={['profileAddress']} noStyle>
          {({ getFieldValue }) =>
            getFieldValue('profileAddress') === true ? (
              <PayerInformation payer={payerInformation()} />
            ) : (
              <>
                <Row gutter="xs">
                  <Col span={12}>
                    <Form.Item<CustomBillingInformation>
                      label="First name"
                      name="firstName"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item<CustomBillingInformation> label="Last name" name="lastName" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                {user?.railsUserData.email ? null : (
                  <Col span={24}>
                    <Form.Item<CustomBillingInformation> label="Email" name="email" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                )}
                <AddressFormContent<CustomBillingInformation> />
                {user?.railsUserData ? (
                  <Form.Item<BillingInformationValues>>
                    <Row gutter="xs">
                      <Col>
                        <Form.Item<CustomBillingInformation> name="profileUpdate" noStyle valuePropName="checked">
                          <Checkbox />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Text size="small">Update information also in my profile</Text>
                      </Col>
                    </Row>
                  </Form.Item>
                ) : null}
              </>
            )
          }
        </Form.Item>
      </Col>
    </Row>
  );
};

export default BillingInformation;
